import { tzOffset } from '@date-fns/tz'
import * as U from '@unimpaired/utils'
import { Country, ICountry } from 'country-state-city'

type Zone = NonNullable<ICountry['timezones']>[number]

/** Each country's offered zones, one per clock, east to west. */
export const PREFERRED_ZONES: Record<string, string[]> = {
  US: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ],
  CA: [
    'America/St_Johns',
    'America/Halifax',
    'America/Blanc-Sablon',
    'America/Toronto',
    'America/Atikokan',
    'America/Winnipeg',
    'America/Regina',
    'America/Edmonton',
    'America/Creston',
    'America/Vancouver',
  ],
  MX: [
    'America/Cancun',
    'America/Matamoros',
    'America/Mexico_City',
    'America/Hermosillo',
    'America/Tijuana',
  ],
  AU: [
    'Australia/Lord_Howe',
    'Australia/Sydney',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Darwin',
    'Australia/Eucla',
    'Australia/Perth',
  ],
  BR: [
    'America/Noronha',
    'America/Sao_Paulo',
    'America/Manaus',
    'America/Rio_Branco',
  ],
}

/** Winter and summer UTC offsets, in minutes. */
/** Region names where the city alone wouldn't explain the clock. */
const ZONE_LABELS: Record<string, string> = {
  'America/Phoenix': 'Mountain Standard Time — Arizona (no daylight saving)',
  'Pacific/Honolulu': 'Hawaii Standard Time',
  'America/Adak': 'Hawaii–Aleutian Standard Time — Aleutian Islands',
}

const offsetsOf = (zoneName: string) => {
  const year = new Date().getUTCFullYear()
  return [0, 6].map((month) =>
    tzOffset(zoneName, new Date(Date.UTC(year, month, 1))),
  )
}

const clockOf = ({ zoneName }: Zone) => offsetsOf(zoneName).join()

const eastToWest = U.sortWith<Zone>([
  U.descend((zone) => offsetsOf(zone.zoneName)[0]),
  U.descend((zone) => offsetsOf(zone.zoneName)[1]),
])

const cityOf = ({ zoneName }: Zone) =>
  zoneName.split('/').at(-1)?.replaceAll('_', ' ')

/** A region override, else the name minus "(North America"-style tails. */
const baseLabel = ({ zoneName, tzName }: Zone) =>
  ZONE_LABELS[zoneName] ?? tzName.replace(/ \(.*$|\[\d+$/, '')

const withLabels = (zones: Zone[]) =>
  zones.map((zone) => ({
    ...zone,
    label:
      zones.filter((other) => baseLabel(other) === baseLabel(zone)).length > 1
        ? `${baseLabel(zone)} — ${cityOf(zone)}`
        : baseLabel(zone),
  }))

/** One zone per clock, east to west; a saved `keepZone` wins its clock. */
export const getCountryTimezones = (countryCode: string, keepZone?: string) => {
  const preferred = PREFERRED_ZONES[countryCode] ?? []
  const rank = ({ zoneName }: Zone) =>
    zoneName === keepZone ? 0 : preferred.includes(zoneName) ? 1 : 2
  const isOffered = ({ zoneName }: Zone) =>
    !preferred.length || preferred.includes(zoneName) || zoneName === keepZone
  return U.pipe(
    Country.getCountryByCode(countryCode)?.timezones ?? [],
    U.filter(isOffered),
    U.sortBy(rank),
    U.uniqBy(clockOf),
    eastToWest,
    withLabels,
  )
}

/** The offered zone on the browser's clock, if any. */
export const getBrowserTimezoneOption = (
  zones: Zone[],
  browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) =>
  U.isUsableVenueTimezone(browserZone)
    ? zones.find((zone) => clockOf(zone) === offsetsOf(browserZone).join())
    : undefined
